import { useCallback, useMemo, useRef, useState } from 'react';

import { MenuIcon } from '@/components/MenuIcon';
import { columnsForViewport, placeSlots, seedLayout, stripLayout, type WidgetSlot } from '@/pages/home/widgetLayout';
import { WidgetMasonry, type LayoutEditing } from '@/pages/home/WidgetMasonry';

/**
 * 배치 커스텀 — 홈 <b>그 자리에서</b> 위젯을 옮기고 늘린다.
 *
 * ## 왜 모달이 아닌가
 *
 * 위젯 관리(무엇을 볼지)는 목록이라 모달이 맞지만, 배치는 <b>보면서 정하는</b> 일이다. 모달 안의
 * 축소판에서 50%를 고르게 하면 사용자는 저장하고 닫은 뒤에야 그게 자기 화면에서 어떻게 보이는지
 * 안다. 그래서 위젯 관리의 「배치 커스텀」 은 모달을 닫고 홈을 편집 상태로 바꾼다.
 *
 * 격자도 보기 화면과 <b>같은 것</b>을 쓴다({@link WidgetMasonry} 의 `editing`) — 편집용 미리보기를
 * 따로 그리면 저장한 뒤의 홈과 어긋나고, 그 어긋남은 늘 저장한 뒤에 발견된다.
 *
 * ## 저장을 눌러야 반영된다
 *
 * 글씨 크기·AI 인사말과 달리 여기는 <b>취소가 필요하다</b>. 위젯 열 몇 개의 폭을 만지다 보면
 * 처음보다 나빠지는 일이 흔한데, 손잡이를 되돌려 원래대로 맞추는 것은 사실상 불가능하다.
 */
export function WidgetLayoutEditor({
  slots,
  onSave,
  onClose,
}: {
  slots: WidgetSlot[];
  onSave: (next: WidgetSlot[]) => void;
  onClose: () => void;
}) {
  // 배치 커스텀을 처음 여는 사람은 지금 화면의 열 수 그대로에서 시작한다(`seedLayout`).
  const [draft, setDraft] = useState<WidgetSlot[]>(() => seedLayout(slots, columnsForViewport()));

  const items = useMemo(() => placeSlots(draft), [draft]);

  const onResize = useCallback((widgetKey: string, widthPercent: number, heightPercent: number) => {
    setDraft((prev) =>
      prev.map((slot) => (slot.widgetKey === widgetKey ? { ...slot, widthPercent, heightPercent } : slot)),
    );
  }, []);

  const onTogglePin = useCallback((widgetKey: string) => {
    setDraft((prev) => prev.map((slot) => (slot.widgetKey === widgetKey ? { ...slot, pinned: !slot.pinned } : slot)));
  }, []);

  /** 끌어 옮긴 자리. 좌표를 그대로 적는다 — 「몇 번째」 가 아니라 「어디」 다. */
  const onMove = useCallback((widgetKey: string, col: number, row: number) => {
    setDraft((prev) => prev.map((slot) => (slot.widgetKey === widgetKey ? { ...slot, col, row } : slot)));
  }, []);

  /*
    격자가 겹침까지 풀고 난 <b>최종</b> 자리. 저장할 때 이것을 쓴다.

    <p>초안(`draft`)에 바로 쓰지 않는 이유는 <b>밀린 것을 굳히지 않기 위해</b>서다. 위젯 하나를
    잠깐 큰 자리 위로 끌면 그 아래 것들이 비켜서는데, 손을 떼지 않고 도로 가져오면 비켰던 것들도
    제자리로 돌아와야 한다. 초안에 적어 버리면 지나간 자리마다 흔적이 남는다.
  */
  const resolved = useRef<Map<string, { col: number; row: number }>>(new Map());
  const onResolve = useCallback((seats: Array<{ widgetKey: string; col: number; row: number }>) => {
    resolved.current = new Map(seats.map((seat) => [seat.widgetKey, { col: seat.col, row: seat.row }]));
  }, []);

  const editing: LayoutEditing = useMemo(
    () => ({ onResize, onTogglePin, onMove, onResolve }),
    [onResize, onTogglePin, onMove, onResolve],
  );

  /** 화면에 보이던 그대로를 저장한다 — 좌표가 없던 위젯은 격자가 찾아 준 자리로 굳는다. */
  const save = () => {
    onSave(
      draft.map((slot) => {
        const seat = resolved.current.get(slot.widgetKey);
        return seat && slot.visible ? { ...slot, col: seat.col, row: seat.row } : slot;
      }),
    );
    onClose();
  };

  return (
    <div className="space-y-3">
      {/*
        머리 줄은 <b>화면에 붙여 둔다</b>(sticky). 위젯이 열 몇 개면 아래쪽 위젯을 만지는 동안
        저장 버튼이 화면 밖으로 나가고, 그러면 맞춰 놓고 위로 올라가는 일이 매번 생긴다.
      */}
      <div className="ofc-bar">
        <span className="flex items-center gap-1.5" style={{ color: 'var(--accent)' }}>
          <MenuIcon name="grid" size={14} />
          <span className="text-ui-md" style={{ fontWeight: 700, color: 'var(--text-primary)' }}>
            배치 커스텀
          </span>
        </span>

        <span className="ofc-bar-tail">
          <button
            className="btn-ghost btn-sm"
            title="폭·높이 배율과 고정을 모두 지우고 기본 배치로 되돌립니다"
            onClick={() => setDraft((prev) => stripLayout(prev))}
          >
            기본 배치로
          </button>
          <button className="btn-secondary btn-sm" onClick={onClose}>
            취소
          </button>
          <button className="btn-primary btn-sm" onClick={save}>
            저장
          </button>
        </span>
      </div>

      <WidgetMasonry items={items} editing={editing} />
    </div>
  );
}
